import React, { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, Button, CalendarIcon, Caption1 } from "@activecollab/components";
import classnames from "classnames";
import classNames from "classnames";
import PropTypes from "prop-types";
import SelectDates from "~/components/Selectors/SelectDates";
import { TaskRescheduleAction } from "~/components/tasks/actions/TaskRescheduleAction";
import { editTask, rescheduleTask } from "~/store/actions/actions.tasks.jsx";
import { getPersonalizedDateFormat, getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { getTaskById, makeIsTaskOverdueSelector } from "~/store/selectors/selector.tasks";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import { formatDateRange, formatDateSmart } from "~/utils/date";
import { propTypes } from "./propTypes";

const TaskDateSelectTrigger = forwardRef(({ value, tooltipValue, onClick, open, isOverdue }, ref) => {
  const { t } = useTranslation();
  return (
    <Button
      className={classnames("show-on-hover c-cooltip c-cooltip--top tw-overflow-visible", {
        open: open || value,
        "tw-w-full tw-px-2": value,
      })}
      variant="text gray"
      active={open}
      ref={ref}
      onClick={onClick}
      data-tooltip={value ? tooltipValue : t("Add Date")}
    >
      {value ? (
        <Caption1
          weight="bold"
          className={classnames({
            "tw-text-red-alert": isOverdue,
          })}
        >
          {value}
        </Caption1>
      ) : (
        <CalendarIcon />
      )}
    </Button>
  );
});
TaskDateSelectTrigger.propTypes = {
  value: PropTypes.string,
  tooltipValue: PropTypes.string,
  onClick: PropTypes.func,
  open: PropTypes.bool,
  isOverdue: PropTypes.bool,
};

TaskDateSelectTrigger.displayName = "TaskDateSelectTrigger";

export const Date = ({ taskId, projectId, className, ...rest }) => {
  const autoReschedule = useSelector((state) => getRawConfigOption(state, "auto_reschedule_enabled"));
  const task = useSelector((state) => getTaskById(state, taskId));
  const canEdit = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));
  const dispatch = useDispatch();
  const format = useSelector(getPersonalizedDateFormat);
  const startOn = task.get("start_on");
  const dueOn = task.get("due_on");

  const formattedDate = formatDateRange(startOn, dueOn, format);
  const fullStartDate = formatDateSmart(startOn, format, true);
  const fullDueDate = formatDateSmart(dueOn, format, true);

  const tooltipText = useMemo(
    () => (task.get("start_on") === task.get("due_on") ? fullDueDate : `${fullStartDate} - ${fullDueDate}`),
    [fullDueDate, fullStartDate, task]
  );

  const onChange = useCallback(
    (dates) => {
      const dateAttributes = { start_on: dates.from, due_on: dates.to };

      if (autoReschedule && dates?.to > 0) {
        return dispatch(rescheduleTask(taskId, dateAttributes));
      }

      return dispatch(editTask(projectId, taskId, { ...dateAttributes }));
    },
    [autoReschedule, dispatch, projectId, taskId]
  );
  const getIsTaskOverdue = useMemo(makeIsTaskOverdueSelector, []);
  const isOverdue = useSelector((state) => getIsTaskOverdue(state, taskId));

  const selected = useMemo(() => {
    return {
      from: startOn,
      to: dueOn,
    };
  }, [startOn, dueOn]);

  return (
    <Body2 className={classNames(className)}>
      {canEdit && !task?.get("is_completed") ? (
        <TaskRescheduleAction
          id={taskId}
          action="onChange"
        >
          <SelectDates
            target={
              <TaskDateSelectTrigger
                value={formattedDate}
                tooltipValue={tooltipText}
                isOverdue={isOverdue}
              />
            }
            userId={task.get("assignee_id")}
            selectedDays={selected}
            onChange={onChange}
            changeMode="atomic"
            position="bottom-end"
            {...rest}
          />
        </TaskRescheduleAction>
      ) : startOn || dueOn ? (
        <Caption1
          weight="bold"
          className={classnames("tw-px-2 c-cooltip c-cooltip--top", {
            "tw-text-red-alert": isOverdue,
          })}
          data-tooltip={tooltipText}
        >
          {formattedDate}
        </Caption1>
      ) : null}
    </Body2>
  );
};

Date.propTypes = propTypes;
